import { useCallback, useState, useEffect } from "react";
import { batch, useDispatch, useSelector } from "react-redux";

import useWorkspaces from "../ducks/user/hooks/useWorkspaces";
import api from "../ducks/user/api";
import actions from "../ducks/user/actions";
import userSelector, { hasBasicProfileSelector } from "../ducks/user/selectors";
import { setAxiosAndGqlToken } from "../helpers/server";
import { numbersFormatter } from "../helpers/number";
import { UIError } from "digiwash-core";
import useCart from "../ducks/cart/hooks/useCart";
import useOrdersNeedsActionList from "../ducks/user/hooks/useOrdersNeedsActionList";
import {
  EVENT_TYPES,
  logEvent,
  logMetrixEvent,
  METRIX_EVENT_TYPES,
} from "../helpers/analytics-helper";

export default useLogin;

export enum LoginStepEnum {
  UNKNOWN = "unknown",
  MOBILE = "mobile",
  VERIFY = "verify",
  PROFILE = "profile",
}
// TODO: Error handling
function useLogin() {
  let { cellphone } = useSelector(userSelector.login);
  let workspaces = useWorkspaces();
  let { revalidate: retrieveNeedsActionOrders } = useOrdersNeedsActionList();
  let [loading, setLoading] = useState(false);
  let dispatch = useDispatch();
  let isLoggedIn = !!useSelector(userSelector.getToken);
  let loginToken = useSelector((state: any) => state.user.login.meta?.token);
  let { retrieve: retrieveCart } = useCart();
  let hasBasicProfile = useSelector(hasBasicProfileSelector);
  let isOnMobile = !loginToken;
  let isOnVerify = loginToken;
  let isOnProfile = isLoggedIn && !hasBasicProfile;
  let onStep = LoginStepEnum.UNKNOWN;
  if (isOnMobile) {
    onStep = LoginStepEnum.MOBILE;
  }
  if (isOnVerify) {
    onStep = LoginStepEnum.VERIFY;
  }
  if (isOnProfile) {
    onStep = LoginStepEnum.PROFILE;
  }

  let login = useCallback(
    async function loginCb(cellphone) {
      setLoading(true);

      try {
        cellphone = numbersFormatter(cellphone, "en");
        let response = await api.login(cellphone);
        dispatch(
          actions.set_phone({
            cellphone,
            meta: {
              ...response,
              expireAt: Date.now() + response.expire_time * 1000,
            },
          })
        );
      } catch (e) {
        //
        let hasErrors = !!e.response?.errors;
        let message = hasErrors
          ? e.response?.errors[0]?.message
          : "مشکلی پیش آمده است.";

        let error: UIError = {
          body: message,
        };
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  let verify = useCallback(
    async function vetifyCb(code) {
      setLoading(true);
      try {
        code = numbersFormatter(code, "en");
        let verifyResponse = await api.login_check(loginToken, code);
        let profileData = await api.get_profile(verifyResponse.token);

        // Because we are getting basket immediatly
        // after login, we need to set it right here
        // then `useEffect` can use `getOrCreateBasket`
        setAxiosAndGqlToken(verifyResponse.token);

        batch(() => {
          dispatch(
            actions.set_token({
              token: verifyResponse.token,
            })
          );
          dispatch(actions.set_profile(profileData));
        });

        // Sending Event
        let { first_name, last_name } = profileData;
        let isSignIn = !!first_name && !!last_name;

        if (isSignIn) {
          logEvent(EVENT_TYPES.LOGIN);
          logMetrixEvent(METRIX_EVENT_TYPES.ACTION_SIGNIN);
        } else {
          logEvent(EVENT_TYPES.SIGNUP);
          logMetrixEvent(METRIX_EVENT_TYPES.ACTION_SIGNUP);
        }

        return verifyResponse;
      } catch (e) {
        //

        let message = e.response?.errors[0]?.message || "مشکلی پیش آمده است.";

        // showMessage({
        //   type: "danger",
        //   message,
        // });

        let error: UIError = {
          body: message,
        };
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [loginToken, loading]
  );

  let resend = useCallback(
    async function resendCb() {
      setLoading(true);
      try {
        let resendResponse = await api.login_resend(loginToken);

        // Because we are getting basket immediatly
        // after login, we need to set it right here
        // then `useEffect` can use `getOrCreateBasket`
        setAxiosAndGqlToken(resendResponse.token);

        batch(() => {
          dispatch(
            actions.set_phone({
              cellphone: cellphone,
              meta: {
                token: resendResponse.data.token,
                expire_time: resendResponse.data.expire_time,
                tries: resendResponse.data.tries,
                sms_number: resendResponse.data.sms_number,
                resend_wait_time: resendResponse.data.resend_wait_time,
                expireAt: Date.now() + resendResponse.data.expire_time * 1000,
              },
            })
          );
        });
      } catch (e) {
        //

        let message = e.response?.errors[0]?.message || "مشکلی پیش آمده است.";

        // showMessage({
        //   type: "danger",
        //   message,
        // });

        let error: UIError = {
          body: message,
        };
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [loginToken, loading]
  );

  useEffect(() => {
    if (isLoggedIn) {
      retrieveCart({ force: true });
      workspaces.refetch();
      retrieveNeedsActionOrders();
    }
  }, [isLoggedIn]);

  return {
    login,
    verify,
    resend,
    loading,
    isLoggedIn,
    step: onStep,
  };
}
